'use client';

import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { LikeBoldIcon } from '@depop/web-ui-kit/Icons/LikeBoldIcon';
import { LikeIcon } from '@depop/web-ui-kit/Icons/LikeIcon';
import { Modal } from '@depop/web-ui-kit/Modal';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';

import productInteractionStyles from '../styles.module.css';

import styles from './styles.module.css';

import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal.tsx';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking.ts';
import { sendBoostedAttributionClick } from '@/modules/impressions/api.ts';
import {
  UPDATE_LIKE_TYPE,
  useUpdateProductLike,
} from '@/modules/likes/hooks/useUpdateProductLike';
import { PRODUCT_INTERACTION_TYPES } from '@/modules/product/constants.ts';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser.ts';

export enum LikesDisplayMode {
  BlackNRedMode,
  WhiteMode,
}

type Props = {
  hasLikedProduct?: boolean;
  productId: number;
  setLikeCount?: Dispatch<SetStateAction<number>>;
  viewMode?: LikesDisplayMode;
  sendBoostedClickAttribution?: boolean;
  isProductBoosted?: boolean;
  likeCount?: number;
};

function LikeCount({ likeCount }: { likeCount: number }) {
  return (
    <Caption
      className={styles.likeCount}
      variant="lg-bold"
      // eslint-disable-next-line no-restricted-syntax
      data-testid="like-count"
    >
      {likeCount > 99 ? '99+' : likeCount}
    </Caption>
  );
}

export function ProductLike({
  productId,
  setLikeCount,
  viewMode = LikesDisplayMode.BlackNRedMode,
  sendBoostedClickAttribution = false,
  isProductBoosted = false,
  hasLikedProduct,
  likeCount,
}: Props) {
  const t = useTranslations('product');
  const [isAuthenticated, user] = useCurrentUser();
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const {
    gtm: { sendEvent },
  } = useExternalTracking();
  const updateLikeMutation = useUpdateProductLike({ userId: user?.id });

  const [isLiked, setIsLiked] = useState(hasLikedProduct);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isOpen, setOpen] = useState(false);

  const hasLikes = likeCount !== undefined && likeCount > 0;

  async function sendProductInteractionATEvent(interactionType: string) {
    sendProductInteractionActionATEvent(productId, interactionType, {
      ...(sendBoostedClickAttribution
        ? {
            isBoosted: isProductBoosted,
          }
        : {}),
    });
  }

  function handleModalOpen(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setOpen(true);
    sendProductInteractionATEvent('ProductLike');
  }

  function addLike() {
    setIsLiked((isLiked) => !isLiked);

    if (setLikeCount) {
      setLikeCount((likeCount: number | undefined) =>
        likeCount ? likeCount + 1 : 1
      );
    }
  }

  function removeLike() {
    setIsLiked((isLiked) => !isLiked);

    if (setLikeCount) {
      setLikeCount((likeCount: number) => likeCount && likeCount - 1);
    }
  }

  function handleBoostedClickAttribution() {
    if (sendBoostedClickAttribution && isProductBoosted) {
      sendBoostedAttributionClick(productId);
    }
  }

  function handleLikeButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setShouldAnimate(true);

    if (isLiked) {
      removeLike();
      updateLikeMutation.mutate(
        { productId, type: UPDATE_LIKE_TYPE.REMOVE },
        {
          onError: addLike,
          onSettled: () => {
            sendProductInteractionATEvent(
              PRODUCT_INTERACTION_TYPES.PRODUCT_UNLIKE
            );
            sendEvent({
              category: 'Product Interaction',
              action: 'unlike product',
              label: productId.toString(),
            });
          },
        }
      );
    } else {
      addLike();
      updateLikeMutation.mutate(
        { productId, type: UPDATE_LIKE_TYPE.ADD },
        {
          onError: removeLike,
          onSettled: () => {
            handleBoostedClickAttribution();
            sendProductInteractionATEvent(
              PRODUCT_INTERACTION_TYPES.PRODUCT_LIKE
            );
            sendEvent({
              category: 'Product Interaction',
              action: 'like product',
              label: productId.toString(),
            });
          },
        }
      );
    }
  }

  useEffect(() => {
    setIsLiked(Boolean(hasLikedProduct));
  }, [hasLikedProduct]);

  const labelMsg = t('LikeProduct', {
    likeStatus: isLiked ? 'Unlike' : 'Like',
    likeCount: likeCount || 0,
  });

  if (!isAuthenticated) {
    return (
      <>
        <button
          className={productInteractionStyles.interactionButton}
          onClick={handleModalOpen}
          aria-label={labelMsg}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="productInteraction__likeButton--unauthenticated"
          aria-haspopup="dialog"
        >
          <LikeIcon
            title="Like Product"
            className={clsx(
              styles.likeIcon,
              styles.emptyLikeIcon,
              viewMode === LikesDisplayMode.WhiteMode && styles.whiteLikeButton
            )}
          />
          {hasLikes && <LikeCount likeCount={likeCount} />}
        </button>
        <Modal
          isOpen={isOpen}
          setOpen={setOpen}
          content={() => (
            <DownloadAppModal downloadAppTrackingId="pdp_like_modal" />
          )}
        />
      </>
    );
  }

  return (
    <>
      <button
        aria-label={labelMsg}
        className={productInteractionStyles.interactionButton}
        onClick={handleLikeButtonClick}
        // eslint-disable-next-line no-restricted-syntax
        data-testid="productInteraction__likeButton"
      >
        {isLiked ? (
          <LikeBoldIcon
            title="Unlike product"
            // eslint-disable-next-line no-restricted-syntax
            data-testid="productInteraction__filledHeart"
            className={clsx(styles.likeIcon, styles.filledLikeIcon, {
              [styles.animatedLike]: shouldAnimate,
              [styles.whiteLikeButton]: viewMode === LikesDisplayMode.WhiteMode,
            })}
          />
        ) : (
          <LikeIcon
            title="Like product"
            // eslint-disable-next-line no-restricted-syntax
            data-testid="productInteraction__emptyHeart"
            className={clsx(
              styles.likeIcon,
              styles.emptyLikeIcon,
              shouldAnimate && styles.animatedLike,
              viewMode === LikesDisplayMode.WhiteMode && styles.whiteLikeButton
            )}
          />
        )}
        {hasLikes && <LikeCount likeCount={likeCount} />}
      </button>
    </>
  );
}
